import { XMarkIcon } from "@heroicons/react/24/outline"
import { StarIcon } from "@heroicons/react/24/solid"
import React, { useMemo } from "react"
import { twMerge } from "tailwind-merge"

interface SocialProofCardTargetViewportClassNameProps {
  containerClassName: string
  buttonClassName: string
  imageClassName: string
  flexColumnClassName: string
}

const DESKTOP_CARD_CLASSNAME: SocialProofCardTargetViewportClassNameProps = {
  containerClassName: "",
  buttonClassName: "right-3 top-3",
  imageClassName: "",
  flexColumnClassName: "gap-1"
}

const MOBILE_CARD_CLASSNAME: SocialProofCardTargetViewportClassNameProps = {
  containerClassName: "h-[80px] items-center gap-2 p-3",
  buttonClassName: "right-2 top-2",
  imageClassName: "h-14 w-14",
  flexColumnClassName: "gap-0.5"
}

export interface SocialProofCardProps {
  imgSrc: string
  preSubHeadingText: string
  /** When true, shows the same solid star as listing cards (Heroicons + text-orange) before the pre-subheading text. */
  preSubHeadingShowListingStar?: boolean
  preSubHeadingClassName?: string
  mainHeadingText: string
  mainHeadingClassName?: string
  postSubHeadingText: string
  postSubHeadingClassName?: string
  destinationUrl?: string
}

export function SocialProofCard({
  props,
  onDismissCallback,
  isMobile
}: Readonly<{
  props: SocialProofCardProps
  onDismissCallback: () => void
  isMobile: boolean
}>) {
  const {
    imgSrc,
    preSubHeadingText,
    preSubHeadingShowListingStar,
    preSubHeadingClassName,
    mainHeadingText,
    mainHeadingClassName,
    postSubHeadingText,
    postSubHeadingClassName,
    destinationUrl
  } = props

  const targetViewPortClassNameObj = useMemo(
    () => (isMobile ? MOBILE_CARD_CLASSNAME : DESKTOP_CARD_CLASSNAME),
    [isMobile]
  )

  const MainHeadingLinkElement = (
    <a
      href={destinationUrl}
      className={twMerge(
        "block w-full truncate text-[16px] font-bold leading-[20px] text-[#0B3954] hover:cursor-pointer hover:text-[#077D83] hover:underline",
        mainHeadingClassName
      )}>
      {mainHeadingText}
    </a>
  )

  const MainHeadingTextElement = (
    <h4
      className={twMerge(
        "block w-full truncate text-[18px] font-bold leading-[20px] text-[#0B3954]",
        mainHeadingClassName
      )}>
      {mainHeadingText}
    </h4>
  )

  return (
    <div className={twMerge("relative flex gap-[12px] p-[12px]", targetViewPortClassNameObj.containerClassName)}>
      <button
        type="button"
        aria-label="Close notification"
        onClick={onDismissCallback}
        className={twMerge(
          "absolute inline-flex h-5 w-5 items-center justify-center rounded-full text-[#9aa3af] hover:bg-[#f0f2f5]",
          targetViewPortClassNameObj.buttonClassName
        )}>
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
      <img
        key={imgSrc}
        src={imgSrc}
        alt="Social Proof Card Image"
        loading="eager"
        decoding="async"
        className={twMerge(
          "h-[72px] w-[72px] rounded-[8px] border object-cover",
          targetViewPortClassNameObj.imageClassName
        )}
      />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col items-start justify-center pr-7",
          targetViewPortClassNameObj.flexColumnClassName
        )}>
        {preSubHeadingShowListingStar ? (
          <p
            className={twMerge(
              "flex w-full min-w-0 items-center gap-1 text-[14px] leading-[18px]",
              preSubHeadingClassName
            )}>
            <StarIcon className="h-[20px] w-[20px] shrink-0 text-orange" aria-hidden />
            <span className="min-w-0 truncate">{preSubHeadingText}</span>
          </p>
        ) : (
          <p className={twMerge("w-full truncate text-[14px] leading-[18px]", preSubHeadingClassName)}>
            {preSubHeadingText}
          </p>
        )}
        {destinationUrl && MainHeadingLinkElement}
        {!destinationUrl && MainHeadingTextElement}
        <p className={twMerge("w-full truncate text-[12px] leading-[16px]", postSubHeadingClassName)}>
          {postSubHeadingText}
        </p>
      </div>
    </div>
  )
}
